import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStripeSession } from './StripeSessionContext';
import { useOnboardingDriver } from './GuidedFormCreation/OnboardingDriverContext';
import PaymentSetup from './PaymentSetup';
import StripeRequirementsReview from './StripeRequirementsReview';
import { PaymentNoticesContext, usePaymentNotices } from './PaymentNoticesContext';
import { selectPaymentNotice, validNotificationCounts } from './paymentNoticeSelector';
import { checkPermission } from '../../utils/auth';
import { P } from '../../utils/permissions';
import { paymentOrganizationKey, paymentProcessor, getStripeAccountId } from '../../utils/paymentReadiness';

/** Above the route cache: inactive Headers never mount more notification observers. */
export function DashboardPaymentNoticesProvider({ value, children }) {
  const { merchant, stripeConnectInstance } = value;
  const session = useStripeSession();
  const state = useOnboardingDriver();
  const refreshPaymentStatus = state?.refreshPaymentStatus;
  const retrySession = session?.retry;
  const canManage = checkPermission(P.MANAGE_PROCESSOR_SETTINGS);
  const processor = paymentProcessor(merchant);
  const isStripe = processor === 'STRIPE';
  const baseScope = `${session?.scope}:${paymentOrganizationKey(merchant)}:${getStripeAccountId(merchant)}:${processor}:${canManage}`;
  // A returning organization is a new visit: old callbacks and open dialogs must
  // not become valid again during A → B → A or permission removal/restoration.
  const visit = useRef({ baseScope, epoch: 0 });
  if (visit.current.baseScope !== baseScope) visit.current = { baseScope, epoch: visit.current.epoch + 1 };
  const scope = `${baseScope}:${visit.current.epoch}`;
  const generation = `${scope}:${session?.generation ?? 0}`;
  const current = useRef(generation); current.current = generation;
  const [record, setRecord] = useState(null);
  const [openScope, setOpenScope] = useState(null);
  const refreshTimer = useRef(null);
  const loadingTimer = useRef(null);
  const enabled = Boolean(isStripe && canManage && stripeConnectInstance);
  const notifications = record?.scope === scope ? record : { phase: enabled ? 'loading' : 'unknown' };
  const phase = isStripe && canManage && session?.error ? 'error' : notifications.phase;
  const refresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => refreshPaymentStatus?.(), 400);
  }, [refreshPaymentStatus]);
  useEffect(() => () => clearTimeout(refreshTimer.current), [generation]);
  const onError = useCallback(() => {
    if (current.current !== generation) return;
    setRecord(old => ({ ...(old?.scope === scope ? old : {}), scope, phase: 'error' }));
    console.warn('[payments] Stripe notification observation unavailable');
  }, [generation, scope]);
  useEffect(() => {
    if (!enabled) return;
    setRecord(old => ({ ...(old?.scope === scope ? old : {}), scope, phase: 'loading' }));
    loadingTimer.current = setTimeout(onError, 15000);
    return () => clearTimeout(loadingTimer.current);
  }, [generation, enabled, scope, onError]);
  // A valid callback cancels the loading deadline; the next generation owns a new deadline.
  const onCounts = useCallback((counts) => {
    if (current.current !== generation) return;
    if (!validNotificationCounts(counts)) { onError(); return; }
    clearTimeout(loadingTimer.current);
    setRecord({ scope, phase: 'ready', ...counts, lastObservedAt: Date.now() });
    refresh();
  }, [generation, scope, onError, refresh]);
  useEffect(() => {
    if (phase !== 'error' || !isStripe || !canManage) return;
    const timer = setTimeout(() => retrySession?.(), 30000);
    return () => clearTimeout(timer);
  }, [phase, isStripe, canManage, retrySession]);
  const onOpenChange = (open) => {
    setOpenScope(open ? scope : null);
    if (!open) refresh();
  };
  const openReview = () => { if (canManage && isStripe) onOpenChange(true); };
  const notice = processor !== 'FINIX' ? selectPaymentNotice(state, { ...notifications, phase }) : null;
  return <PaymentNoticesContext.Provider value={{ merchant, notifications: { ...notifications, phase }, notice, openReview }}>
    {children}
    <PaymentSetup merchant={merchant} showNotice={false} />
    {isStripe && canManage && <StripeRequirementsReview open={openScope === scope} onOpenChange={onOpenChange}
      enabled={enabled} generation={generation} phase={phase} total={notifications.total}
      onNotificationsChange={onCounts} onError={onError} onRetry={session?.retry}
      onContinueSetup={() => { onOpenChange(false); state?.setPaymentSetupOpen(true); }} />}
  </PaymentNoticesContext.Provider>;
}

export default function DashboardPaymentNotices() {
  const notices = usePaymentNotices();
  const location = useLocation();
  if (!notices || location.pathname.replace(/\/$/, '') === '/settings/compliance') return null;
  return <div className="mx-4 lg:ml-0"><PaymentSetup merchant={notices.merchant} showDialog={false} /></div>;
}
